import React, { useState, useRef, useEffect } from 'react';
import styles from './CustomVideoPlayer.module.css';
import { Play, Pause, Download, Maximize, Volume2, VolumeX } from 'lucide-react';
import CustomTooltip from '../Tooltip/Tooltip';

const CustomVideoPlayer = ({ blob }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isMuted, setIsMuted] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const progressRef = useRef(null);

    useEffect(() => {
        if (blob) {
            if (videoUrl) {
                URL.revokeObjectURL(videoUrl);
            }

            const newUrl = URL.createObjectURL(blob);
            setVideoUrl(newUrl);

            if (videoRef.current) {
                videoRef.current.src = newUrl;
                videoRef.current.load();
            }

            setCurrentTime(0);
            setIsPlaying(false);
            setIsLoading(false);

            return () => {
                if (newUrl) {
                    URL.revokeObjectURL(newUrl);
                }
            };
        }
        // eslint-disable-next-line
    }, [blob]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleTimeUpdate = () => setCurrentTime(video.currentTime);
        const handleLoadedMetadata = () => setDuration(video.duration);
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(video.duration);
        };
        const handleError = (error) => {
            console.error('Video error:', error);
            setIsLoading(false);
        };

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('error', handleError);

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('ended', handleEnded);
            video.removeEventListener('error', handleError);
        };
    }, []);

    const togglePlay = async () => {
        if (!videoRef.current) return;

        try {
            if (isPlaying) {
                await videoRef.current.pause();
            } else {
                await videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        } catch (error) {
            console.error('Error toggling play:', error);
        }
    };

    const handleProgressClick = (e) => {
        if (!videoRef.current || !duration) return;

        const bounds = progressRef.current.getBoundingClientRect();
        const x = e.clientX - bounds.left;
        const percentage = x / bounds.width;
        const newTime = percentage * duration;

        videoRef.current.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const toggleFullScreen = () => {
        if (!containerRef.current) return;

        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            containerRef.current.requestFullscreen();
        }
    };

    const toggleMute = () => {
        if (!videoRef.current) return;
        videoRef.current.muted = !videoRef.current.muted;
        setIsMuted(!isMuted);
    };

    const handleDownload = () => {
        if (blob) {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `presentation-recording-${Math.floor(duration)}s.mp4`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
    };

    const formatTime = (time) => {
        if (!isFinite(time) || time === 0) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <div className={styles.playerContainer} ref={containerRef}>
            <div className={styles.videoWrapper}>
                <video
                    ref={videoRef}
                    className={styles.video}
                    onClick={togglePlay}
                    preload="auto"
                />
            </div>

            <div className={styles.controls}>
                <button
                    className={`${styles.controlButton} ${styles.playButton} ${isLoading ? styles.disabled : ''}`}
                    onClick={togglePlay}
                    disabled={isLoading}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                    <span className={styles.buttonIcon}>
                        {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                    </span>
                </button>

                <div className={styles.progressContainer}>
                    <div
                        className={styles.progressBar}
                        ref={progressRef}
                        onClick={handleProgressClick}
                    >
                        <div
                            className={styles.progress}
                            style={{
                                width: `${duration ? (currentTime / duration) * 100 : 0}%`
                            }}
                        />
                    </div>
                    <div className={styles.timeDisplay}>
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                    </div>
                </div>

                <button
                    className={`${styles.controlButton} ${styles.volumeButton}`}
                    onClick={toggleMute}
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                >
                    <span className={styles.buttonIcon}>
                        {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                    </span>
                </button>

                <CustomTooltip text="Video files are temporary and will not be saved in the application. Download to keep a permanent copy on your device." isDownloadButton={true}>
                    <button
                        className={`${styles.controlButton} ${styles.downloadButton}`}
                        onClick={handleDownload}
                        aria-label="Download recording"
                    >
                        <span className={styles.buttonIcon}><Download size={14} /></span>
                    </button>
                </CustomTooltip>

                <button
                    className={`${styles.controlButton} ${styles.fullscreenButton}`}
                    onClick={toggleFullScreen}
                    aria-label="Toggle fullscreen"
                >
                    <span className={styles.buttonIcon}><Maximize size={14} /></span>
                </button>
            </div>
        </div>
    );
};

export default CustomVideoPlayer;